import React, { useState } from 'react';

interface DocumentViewerProps {
  documentUrl: string;
  fileName: string;
  onClose: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ documentUrl, fileName, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [convertedUrl, setConvertedUrl] = useState<string | null>(null);
  const [conversionError, setConversionError] = useState<string | null>(null);
  
  console.log('🔍 DocumentViewer Debug:', {
    documentUrl: documentUrl?.substring(0, 100) + '...',
    fileName,
    urlType: documentUrl?.startsWith('data:') ? 'data' : documentUrl?.startsWith('blob:') ? 'blob' : 'other',
    isNull: documentUrl === null,
    isUndefined: documentUrl === undefined,
    isEmpty: documentUrl === '',
    isError: documentUrl === 'error'
  });

  // Try to convert URL to data URL if it's not already one
  React.useEffect(() => {
    const convertUrlToDataUrl = async () => {
      if (documentUrl.startsWith('data:') || documentUrl.startsWith('blob:')) {
        console.log('🔍 URL is already data/blob, no conversion needed');
        setConvertedUrl(documentUrl);
        setIsLoading(false);
        return;
      }

      if (documentUrl === 'error' || !documentUrl || documentUrl === null || documentUrl === '') {
        console.log('🔍 URL is error, null, empty, or undefined, cannot convert');
        setConversionError('File not found or corrupted in storage');
        setIsLoading(false);
        return;
      }

      try {
        console.log('🔍 Attempting to convert URL to data URL:', documentUrl.substring(0, 50) + '...');
        
        // Try to fetch the file
        const response = await fetch(documentUrl, {
          method: 'GET',
          mode: 'cors', // Enable CORS
          cache: 'no-cache',
          headers: {
            'Accept': '*/*',
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        console.log('🔍 Fetched blob:', blob.size, 'bytes, type:', blob.type);
        
        // Check if blob is empty
        if (blob.size === 0) {
          throw new Error('File is empty or corrupted');
        }
        
        // Convert to data URL
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          console.log('🔍 Converted to data URL, length:', dataUrl.length);
          setConvertedUrl(dataUrl);
          setIsLoading(false);
        };
        reader.onerror = (error) => {
          console.error('🔍 FileReader error:', error);
          setConversionError('Failed to convert file to viewable format');
          setIsLoading(false);
        };
        reader.readAsDataURL(blob);
        
      } catch (error) {
        console.error('🔍 URL conversion failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setConversionError(`Failed to load file: ${errorMessage}. The file may be corrupted, missing, or inaccessible.`);
        setIsLoading(false);
      }
    };

    convertUrlToDataUrl();
  }, [documentUrl]);

  // Set a timeout to show download option if loading takes too long
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        console.log('🔍 DocumentViewer timeout - showing download option');
        setLoadTimeout(true);
        setIsLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timer);
  }, [isLoading]);

  const getFileType = (url: string) => {
    if (url.startsWith('data:')) {
      const mimeType = url.split(';')[0].split(':')[1];
      return mimeType;
    } else if (url.startsWith('blob:')) {
      // For blob URLs, we need to check the file extension
      return 'unknown';
    } else if (url.startsWith('http://') || url.startsWith('https://')) {
      // For HTTP URLs, try to determine type from file extension
      return 'unknown';
    } else if (url === 'error') {
      // Handle error case
      return 'error';
    }
    return 'unknown';
  };

  const getFileExtension = (fileName: string) => {
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  const getFileTypeFromExtension = (extension: string) => {
    const mimeTypes: Record<string, string> = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'txt': 'text/plain',
      'csv': 'text/csv',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };
    return mimeTypes[extension] || 'unknown';
  };

  // Use converted URL if available, otherwise use original
  const currentUrl = convertedUrl || documentUrl;
  const fileType = getFileType(currentUrl);
  const fileExtension = getFileExtension(fileName);
  
  // Handle all URL types by checking file extension when fileType is unknown
  const actualFileType = fileType === 'unknown' 
    ? getFileTypeFromExtension(fileExtension)
    : fileType;

  const renderDocument = () => {
    console.log('🔍 DocumentViewer renderDocument:', {
      fileType,
      fileExtension,
      actualFileType,
      currentUrl: currentUrl?.substring(0, 50) + '...',
      originalUrl: documentUrl?.substring(0, 50) + '...',
      converted: !!convertedUrl,
      conversionError,
      urlStartsWith: {
        data: currentUrl?.startsWith('data:'),
        blob: currentUrl?.startsWith('blob:'),
        http: currentUrl?.startsWith('http://') || currentUrl?.startsWith('https://')
      }
    });

    // Always show download option prominently
    const downloadButton = (
          <div className="bg-blue-50 border-t border-blue-200 p-4 text-center">
            <p className="text-sm text-blue-800 mb-3">
              Document preview may not work properly. Click below to download the file:
            </p>
            <div className="space-y-2">
              <button
                onClick={() => {
                  try {
                    if (currentUrl.startsWith('data:') || currentUrl.startsWith('blob:')) {
                      // For data and blob URLs, create a download link
                      const link = document.createElement('a');
                      link.href = currentUrl;
                      link.download = fileName;
                      link.style.display = 'none';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    } else {
                      // For other URLs, try to open in new tab first, then download
                      const newWindow = window.open(currentUrl, '_blank');
                      if (!newWindow) {
                        // If popup blocked, try direct download
                        const link = document.createElement('a');
                        link.href = currentUrl;
                        link.download = fileName;
                        link.target = '_blank';
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }
                  } catch (error) {
                    console.error('Download error:', error);
                    alert('Download failed. Please try opening the file in a new tab.');
                    window.open(currentUrl, '_blank');
                  }
                }}
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                📥 Download {fileName}
              </button>
              <button
                onClick={() => {
                  try {
                    // Force open in new tab for any URL type
                    window.open(currentUrl, '_blank');
                  } catch (error) {
                    console.error('Open error:', error);
                    alert('Unable to open file. Please try downloading instead.');
                  }
                }}
                className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                🔗 Open in New Tab
              </button>
              {conversionError && (
                <button
                  onClick={() => {
                    // Reset state and try again
                    setConversionError(null);
                    setConvertedUrl(null);
                    setIsLoading(true);
                    setLoadTimeout(false);
                  }}
                  className="inline-block px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium"
                >
                  🔄 Try Again
                </button>
              )}
            </div>
          </div>
    );

    if (actualFileType.startsWith('image/')) {
      return (
        <div className="w-full h-full flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <img
              src={currentUrl}
              alt={fileName}
              className="max-w-full max-h-full object-contain"
              onLoad={() => {
                console.log('🔍 Image loaded successfully');
                setIsLoading(false);
              }}
              onError={(e) => {
                console.error('🔍 Image failed to load:', e);
                setIsLoading(false);
              }}
            />
          </div>
          {downloadButton}
        </div>
      );
    } else if (actualFileType === 'application/pdf' || fileExtension === 'pdf') {
      return (
        <div className="w-full h-full flex flex-col">
          <div className="flex-1">
            {conversionError ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 p-8">
                <div className="text-6xl mb-4">📄</div>
                <p className="text-lg mb-2">PDF Preview Unavailable</p>
                <p className="text-sm mb-4">File: {fileName}</p>
                <p className="text-sm text-gray-600 mb-6">
                  Unable to load PDF preview. This might be due to CORS restrictions or file format issues.
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Error: {conversionError}
                </p>
              </div>
            ) : (
              <iframe
                src={currentUrl}
                className="w-full h-full border-0"
                title={`PDF Viewer - ${fileName}`}
                onLoad={() => {
                  console.log('🔍 PDF loaded successfully');
                  setIsLoading(false);
                }}
                onError={(e) => {
                  console.error('🔍 PDF failed to load:', e);
                  setIsLoading(false);
                }}
              />
            )}
          </div>
          {downloadButton}
        </div>
      );
    } else if (actualFileType === 'text/plain' || actualFileType === 'text/csv' || 
               fileExtension === 'txt' || fileExtension === 'csv') {
      return (
        <div className="w-full h-full flex flex-col">
          <div className="flex-1 p-4">
            <pre className="whitespace-pre-wrap text-sm font-mono bg-gray-50 p-4 rounded h-full overflow-auto">
              {(() => {
                try {
                  if (currentUrl.startsWith('data:')) {
                    const base64Data = currentUrl.split(',')[1];
                    if (base64Data) {
                      return atob(base64Data);
                    }
                  }
                  return 'Text content not available for preview';
                } catch (error) {
                  console.error('Error decoding text content:', error);
                  return 'Error loading text content. Please download the file to view it.';
                }
              })()}
            </pre>
          </div>
          {downloadButton}
        </div>
      );
    } else if (actualFileType === 'error' || documentUrl === 'error' || documentUrl === null || !documentUrl || documentUrl === '' || conversionError) {
      // Handle error case
      return (
        <div className="w-full h-full flex flex-col">
          <div className="flex-1 flex flex-col items-center justify-center text-red-500 p-8">
            <div className="text-6xl mb-4">❌</div>
            <p className="text-lg mb-2">Error Loading File</p>
            <p className="text-sm mb-4">File: {fileName}</p>
            <p className="text-sm text-red-600 mb-6">
              {conversionError || 'There was an error loading this file. The file may be corrupted or missing from storage.'}
            </p>
            <div className="text-xs text-gray-500 mb-4">
              <p>Original URL: {documentUrl || 'null/undefined'}</p>
              <p>Current URL: {currentUrl || 'null/undefined'}</p>
              <p>Conversion Error: {conversionError || 'None'}</p>
              <p>URL Type: {documentUrl?.startsWith('data:') ? 'data' : documentUrl?.startsWith('blob:') ? 'blob' : documentUrl?.startsWith('http') ? 'http' : 'unknown'}</p>
            </div>
          </div>
          <div className="bg-red-50 border-t border-red-200 p-4 text-center">
            <p className="text-sm text-red-800 mb-3">
              Unable to load file. This usually means the file was not properly uploaded or has been deleted from storage.
            </p>
            <div className="space-y-2">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Close
              </button>
              <button
                onClick={() => {
                  // Try to refresh the page to reload the file
                  window.location.reload();
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium ml-2"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      // For all other file types, show download option immediately
      return (
        <div className="w-full h-full flex flex-col">
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-8">
            <div className="text-6xl mb-4">📄</div>
            <p className="text-lg mb-2">Document Preview Not Available</p>
            <p className="text-sm mb-4">File: {fileName}</p>
            <p className="text-sm mb-6">File type: {actualFileType || fileExtension || 'Unknown'}</p>
            <p className="text-sm text-gray-600 mb-6">
              This file type cannot be previewed in the browser. Please download it to view the content.
            </p>
          </div>
          {downloadButton}
        </div>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800 truncate">
            {fileName}
          </h3>
          <div className="flex items-center space-x-2">
            <a
              href={documentUrl}
              download={fileName}
              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            >
              Download
            </a>
            <button
              onClick={onClose}
              className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
            >
              Close
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative">
          {isLoading && !loadTimeout && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading document...</p>
                {conversionError && (
                  <p className="text-red-600 text-sm mt-2">Conversion error: {conversionError}</p>
                )}
                {!convertedUrl && !conversionError && (
                  <p className="text-blue-600 text-sm mt-2">Converting file to viewable format...</p>
                )}
              </div>
            </div>
          )}
          
          {loadTimeout && (
            <div className="absolute inset-0 flex items-center justify-center bg-yellow-50">
              <div className="text-center max-w-md">
                <div className="text-6xl mb-4">⏰</div>
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Loading Timeout</h3>
                <p className="text-sm text-yellow-700 mb-4">
                  The document is taking too long to load. This might be due to CORS restrictions or file size.
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      try {
                        if (documentUrl.startsWith('data:') || documentUrl.startsWith('blob:')) {
                          // For data and blob URLs, create a download link
                          const link = document.createElement('a');
                          link.href = documentUrl;
                          link.download = fileName;
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        } else {
                          // For other URLs, try to open in new tab first, then download
                          const newWindow = window.open(documentUrl, '_blank');
                          if (!newWindow) {
                            // If popup blocked, try direct download
                            const link = document.createElement('a');
                            link.href = documentUrl;
                            link.download = fileName;
                            link.target = '_blank';
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }
                      } catch (error) {
                        console.error('Download error:', error);
                        alert('Download failed. Please try opening the file in a new tab.');
                        window.open(documentUrl, '_blank');
                      }
                    }}
                    className="block w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Download File
                  </button>
                  <button
                    onClick={onClose}
                    className="block w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {!isLoading && !loadTimeout && (
            <div className="w-full h-full">
              {renderDocument()}
            </div>
          )}
          
          {/* Debug info */}
          <div className="absolute top-2 right-2 bg-black bg-opacity-75 text-white text-xs p-2 rounded max-w-md">
            <div>URL: {documentUrl?.substring(0, 30)}...</div>
            <div>Type: {fileType} | Ext: {fileExtension}</div>
            <div>Actual: {actualFileType} | Timeout: {loadTimeout ? 'Yes' : 'No'}</div>
            <div>URL Type: {documentUrl?.startsWith('data:') ? 'data' : documentUrl?.startsWith('blob:') ? 'blob' : 'other'}</div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 text-sm text-gray-600">
          <div className="flex items-center justify-between">
            <span>File Type: {fileType === 'unknown' && documentUrl.startsWith('blob:') 
              ? getFileTypeFromExtension(fileExtension) 
              : fileType}</span>
            <span>Size: {documentUrl.startsWith('data:') 
              ? `${Math.round(documentUrl.length * 0.75 / 1024)} KB (base64)` 
              : 'Unknown'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;





